import { useEffect, useState, FC } from 'react'
import { Area, AreaChart, YAxis } from 'recharts'
import cn from 'classnames'

import { useAppSelector } from 'store/hooks'
import { selectCurrency } from 'store/reducers/currencies'

import FilterButton from 'pages/account/components/deposit-card/header/filter-button'
import SortingButton from 'pages/account/components/deposit-card/header/sorting-button'

import { useRateInfo } from 'pages/account/wallets/info/components/chart/use/useRateInfo'

import { CountdownTimer, STATUS } from 'components/ui/countdown-timer'

import { NetworkLabel } from 'components/ui/network-label'

import { ELoanStatuses } from '../../../../store/fetchers/loans/enum'

import { EEarningStatuses } from '../../../../store/fetchers/earnings/enum'

import { CopyButtonWithSuccess } from '../../../../components/ui/copy-button-with-success'

import Tooltip from '../../../../components/global/tooltip'

import { floorNumber } from '../../../../utils/floor-number'

import { formatNumber } from '../../../../utils/formatters'

import type {
  ChartCellProps,
  AmountCellProps,
  CellProps,
  ColumnsProps,
  ColumnHeaderCellProps,
  ContainerProps,
  StatusCellProps,
} from './types'

import styles from './styles.module.scss'
import { CurrencyCellProps } from './types'
import { useTranslation } from 'utils/use-translation'

const Cell: FC<CellProps> = ({ children, className }) => {
  return <div className={cn(styles.cell, className)}>{children}</div>
}

const Columns: FC<ColumnsProps> = ({ columns, loan }) => {
  return (
    <div
      className={cn(styles.cellNames, {
        [styles.loan]: loan,
        [styles.gray]: loan?.status === ELoanStatuses.CLOSED || loan?.status === ELoanStatuses.LIQUIDATED,
        [styles.red]:
          loan?.current_zone <= 0 &&
          loan?.current_zone !== null &&
          loan?.status !== ELoanStatuses.CLOSED &&
          loan?.status !== ELoanStatuses.LIQUIDATED,
      })}
    >
      {columns.map((col) => (
        <Cell key={col}>
          <p>{col}</p>
        </Cell>
      ))}
    </div>
  )
}

const CurrencyCell: FC<CurrencyCellProps> = ({ className, ticker, network }) => {
  const currencyInfo = useAppSelector(selectCurrency)(ticker, network)

  return (
    <Cell className={cn(styles.currencyCell, className)}>
      <img src={currencyInfo?.logo_url} alt={`${ticker} logo`} className={styles.depositAmountLogo} />
      <div className={styles.currencyCell__amountAndAdditionalContainer}>
        <p className={styles.currencyCellTicker}>{ticker}</p>
        <div className={styles.currencyCell__secondaryTextContainer} style={{ alignItems: 'center' }}>
          <span className={styles.currencyCell__secondaryTextContainer_text}>{currencyInfo?.name}</span>
          <NetworkLabel currency={currencyInfo} className={styles.currencyCell__network} />
        </div>
      </div>
    </Cell>
  )
}

const AmountCell: FC<AmountCellProps> = ({ className, amount, ticker, network, additionalAmount, loan }) => {
  const currencyInfo = useAppSelector(selectCurrency)(ticker, network)

  return (
    <Cell
      className={cn(styles.depositAmount, className, {
        [styles.gray]: loan?.status === ELoanStatuses.CLOSED || loan?.status === ELoanStatuses.LIQUIDATED,
        [styles.red]:
          loan?.current_zone <= 0 &&
          loan?.current_zone !== null &&
          loan?.status !== ELoanStatuses.CLOSED &&
          loan?.status !== ELoanStatuses.LIQUIDATED,
      })}
    >
      <>
        <img src={currencyInfo?.logo_url} alt={`${ticker} logo`} className={styles.depositAmountLogo} />
        <div className={styles.amountAndAdditionalContainer}>
          <div className={styles.amountContainer}>
            <p className={styles.depositAmountValue}>
              {amount.length > 12 ? amount.slice(0, 12) + '...' : amount}&nbsp;
            </p>
            <p className={styles.depositAmountTicker}>{ticker}</p>
            {amount.length > 12 && (
              <Tooltip className={styles.tooltip}>
                {formatNumber(floorNumber(Number(amount.replace(/\s/g, '')), 6))}
              </Tooltip>
            )}
          </div>
          <p className={styles.depositAmountAdditional}>{additionalAmount}</p>
        </div>
      </>
    </Cell>
  )
}

const ChartCell: FC<ChartCellProps> = ({ currency, period, className, color }) => {
  const { graphData } = useRateInfo({ currency: currency, period: period })
  const minValue = Math.min(...graphData.map((item) => item.rate))
  const maxValue = Math.max(...graphData.map((item) => item.rate))
  return (
    <Cell className={className}>
      <AreaChart width={120} height={36} data={graphData}>
        <defs>
          <linearGradient id={currency && currency.code} x1="0" y1="0" x2="0" y2="1">
            <stop offset="3%" stopColor={color} stopOpacity={0.5} />
            <stop offset="97%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis width={0} domain={[minValue, maxValue]} />
        <Area type="monotone" dataKey="rate" stroke={color} fillOpacity={0.5} fill={`url(#${currency.code})`} />
      </AreaChart>
    </Cell>
  )
}

const StatusCell: FC<StatusCellProps> = ({ className, status, id, createdAt, loan }) => {
  const { t } = useTranslation()

  return (
    <Cell className={styles.status}>
      <div className={cn(styles.text, className)}>
        <p>{status === t('failed') ? t('active') : t(status.toLowerCase())}</p>
        {createdAt && <CountdownTimer type={STATUS} creationDate={new Date(createdAt)} />}
      </div>
      {loan &&
        loan.status !== ELoanStatuses.CLOSED &&
        loan.status !== ELoanStatuses.LIQUIDATED &&
        (loan.current_zone === 1 || loan.current_zone === 2) && (
          <p className={styles.zone}>
            <span className={styles.orange}>{t('orange_capital')}</span> {t('zone_info')}
          </p>
        )}

      {loan &&
        loan.status !== ELoanStatuses.CLOSED &&
        loan.status !== ELoanStatuses.LIQUIDATED &&
        loan.current_zone > 2 && (
          <p className={styles.zone}>
            <span className={styles.green}>{t('green_capital')}</span> {t('zone_info')}
          </p>
        )}

      {loan &&
        loan.status !== ELoanStatuses.CLOSED &&
        loan.status !== ELoanStatuses.LIQUIDATED &&
        loan.current_zone === 0 && (
          <p className={styles.zone}>
            <span className={styles.red}>{t('red_capital')}</span> {t('zone_info')}
          </p>
        )}

      {loan &&
        loan.status !== ELoanStatuses.CLOSED &&
        loan.status !== ELoanStatuses.LIQUIDATED &&
        loan.current_zone === -1 && (
          <p className={styles.zone}>
            <span className={styles.red}>{t('liquidation')}</span> {t('zone_info')}
          </p>
        )}

      {id && (
        <div className={styles.idWrapper}>
          <p className={styles.id}>ID: {id}</p>
          <CopyButtonWithSuccess className={styles.copyBtn} value={id} />
        </div>
      )}
    </Cell>
  )
}

const Container: FC<ContainerProps> = ({ onClick, children, columns, loan, saving }) => {
  const [containerClassName, setContainerClassName] = useState<string>(null)

  useEffect(() => {
    if (loan && loan.current_zone <= 0) {
      setContainerClassName(styles.red)
    }

    if (loan && (loan.current_zone > 0 || loan.current_zone === null)) {
      setContainerClassName(styles.active)
    }

    if (loan && (loan.status === ELoanStatuses.CLOSED || loan.status === ELoanStatuses.LIQUIDATED)) {
      setContainerClassName(styles.closed)
    }
  }, [loan])

  useEffect(() => {
    if (saving) {
      if (saving.status === EEarningStatuses.CLOSED) {
        setContainerClassName(styles.closed)
      } else {
        setContainerClassName(styles.active)
      }
    }
  }, [saving])

  return (
    <div className={cn(styles.container, containerClassName)} onClick={onClick}>
      <Columns columns={columns} loan={loan} />

      <div className={cn(styles.infoContainer, { [styles.loan]: loan })}>{children}</div>
    </div>
  )
}

const ColumnsHeader: FC<CellProps> = ({ children, className }) => {
  return <div className={className || styles.columnsHeader}>{children}</div>
}

const ColumnHeaderCell: FC<ColumnHeaderCellProps> = ({ title, onClick, isActive, sortingState, width, className }) => {
  return (
    <div className={cn(styles.columnHeaderCell, className)} style={width ? { width: `${width}px` } : {}}>
      <button
        type="button"
        className={cn(styles.columnHeaderCellPressable, {
          [styles.selected]: sortingState === 1 || sortingState === 2,
          [styles.hoverable]: onClick,
        })}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        <p className={cn(styles.columnHeaderCellTitle, { [styles.clickable]: onClick })}>{title}</p>
        {sortingState !== undefined
          ? onClick && (
              <SortingButton
                selected={sortingState === 1 || sortingState === 2}
                className={styles.columnHeaderCellSortButton}
                sortingState={sortingState}
              />
            )
          : onClick && <FilterButton className={styles.columnHeaderCellSortButton} isActive={isActive} />}
      </button>
    </div>
  )
}

export default {
  Container,
  Cell,
  AmountCell,
  StatusCell,
  ColumnsHeader,
  ColumnHeaderCell,
  ChartCell,
  CurrencyCell,
}
